import { useEffect, useState } from "react";
import { bySex } from "@/lib/bySex";
import { api } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { Loader2, Sparkles } from "lucide-react";
import ProductCard from "@/components/ProductCard";

const CATS = ["Tutti", "Corredini", "Completi", "Coperte", "Bagnetto", "Accessori"];
const SEASON_BY_MONTH = { 1: "Inverno", 2: "Inverno", 3: "Primavera", 4: "Primavera", 5: "Primavera", 6: "Estate", 7: "Estate", 8: "Estate", 9: "Autunno", 10: "Autunno", 11: "Autunno", 12: "Inverno" };
const CURRENT_SEASON = SEASON_BY_MONTH[new Date().getMonth() + 1];

export default function Catalog() {
  const { addToWishlist, inWishlist, profile } = useProfile();
  const [category, setCategory] = useState("Tutti");
  const [size, setSize] = useState("");
  const [material, setMaterial] = useState("");
  const [seasonOn, setSeasonOn] = useState(false);
  const [sizes, setSizes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(12);
  const [recommended, setRecommended] = useState(null);

  const canFilterSex = profile && (profile.sex === "femmina" || profile.sex === "maschio");
  const activeSex = canFilterSex ? profile.sex : undefined;

  const subjectLabel = bySex(profile?.sex, { femmina: "lei", maschio: "lui", other: "il bebè" });

  useEffect(() => {
    api.get("/catalog/filters").then(({ data }) => {
      setSizes(data.sizes || []);
      setMaterials(data.materials || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!profile) return;
    api.get("/catalog/recommended", { params: { profile_id: profile.id } })
      .then(({ data }) => setRecommended(data))
      .catch(() => setRecommended(null));
  }, [profile]);

  useEffect(() => {
    setLoading(true);
    setVisible(12);
    api
      .get("/catalog", { params: { category, size: size || undefined, material: material || undefined, sex: activeSex, season: seasonOn ? CURRENT_SEASON : undefined } })
      .then(({ data }) => setProducts(data.products || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, size, material, activeSex, seasonOn]);

  return (
    <section id="catalogo" className="bg-[#F4EFEA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 sm:py-14 lg:py-20">
        {recommended && recommended.products.length > 0 && (
          <div className="mb-14" data-testid="recommended-section">
            <div className="flex items-center gap-2 text-[#C87D75] mb-2">
              <Sparkles size={18} />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold">Consigliati per {subjectLabel}</span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#2D2825]">Già pronti per il suo arrivo</h2>
            <p className="text-[#5C534E] mt-2">
              Taglia <strong>{recommended.recommended_size}</strong> · stagione <strong>{recommended.season}</strong> — selezionati su misura.
            </p>
            <div className="mt-8 mb-rail sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {recommended.products.map((p, i) => (
                <ProductCard key={p.id} product={p} onAdd={addToWishlist} inList={inWishlist(p.id)}
                  delay={(i % 4) * 0.05} highlight testid="recommended-product-card" />
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8C7F78]">Catalogo live</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#2D2825] mt-2">La nostra collezione in cotone</h2>
            <p className="text-[#5C534E] mt-2">
              {activeSex
                ? `Capi selezionati per ${activeSex === "femmina" ? "la tua bambina" : "il tuo bambino"} (e capi neutri)`
                : "Prezzi e disponibilità aggiornati da minimebaby.it"}
            </p>
          </div>
          <div className="flex flex-wrap gap-3 self-start">
            <button
              data-testid="catalog-season-filter" onClick={() => setSeasonOn((v) => !v)}
              className="inline-flex items-center gap-2 rounded-full h-11 px-5 text-sm font-display border transition-colors"
              style={{
                background: seasonOn ? "#EAF0EC" : "#fff",
                color: seasonOn ? "#4E7363" : "#5C534E",
                borderColor: seasonOn ? "#A7C4B5" : "#EAE3DC",
              }}
            >
              <Sparkles size={15} /> Adatto a {CURRENT_SEASON.toLowerCase()}
            </button>
            <select
              data-testid="catalog-material-filter" value={material} onChange={(e) => setMaterial(e.target.value)}
              className="rounded-full bg-white border border-[#EAE3DC] h-11 px-5 text-sm"
            >
              <option value="">Tutti i tessuti</option>
              {materials.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select
              data-testid="catalog-size-filter" value={size} onChange={(e) => setSize(e.target.value)}
              className="rounded-full bg-white border border-[#EAE3DC] h-11 px-5 text-sm"
            >
              <option value="">Tutte le taglie</option>
              {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-10" data-testid="catalog-category-filter">
          {CATS.map((c) => (
            <button
              key={c} data-testid={`category-${c}`} onClick={() => setCategory(c)}
              className="rounded-full px-5 py-2 text-sm font-display transition-all border"
              style={{
                background: category === c ? "#2D2825" : "#fff",
                color: category === c ? "#fff" : "#5C534E",
                borderColor: category === c ? "#2D2825" : "#EAE3DC",
              }}
            >
              {c}
            </button>
          ))}
          {canFilterSex && (
            <span
              data-testid="sex-filter-active"
              className="ml-auto inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-display border"
              style={{
                background: profile.sex === "maschio" ? "#EBF3F8" : "#F9ECE9",
                color: profile.sex === "maschio" ? "#4A7496" : "#C87D75",
                borderColor: profile.sex === "maschio" ? "#9DC0DB" : "#E5A8A0",
              }}
            >
              Solo capi {profile.sex === "maschio" ? "da maschio" : "da femmina"} + neutri
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#8C7F78] py-16 justify-center" data-testid="catalog-loading">
            <Loader2 className="animate-spin" size={20} /> Caricamento catalogo...
          </div>
        ) : products.length === 0 ? (
          <div className="text-center text-[#8C7F78] py-16" data-testid="catalog-empty">
            Nessun prodotto trovato per questi filtri.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 lg:gap-8">
              {products.slice(0, visible).map((p, i) => (
                <ProductCard key={p.id} product={p} onAdd={addToWishlist} inList={inWishlist(p.id)}
                  delay={(i % 4) * 0.05} />
              ))}
            </div>
            {visible < products.length && (
              <div className="text-center mt-10">
                <button
                  data-testid="load-more-button" onClick={() => setVisible((v) => v + 12)}
                  className="rounded-full border border-[#D2C5B9] text-[#5C534E] hover:bg-white px-8 py-3 text-sm transition-colors"
                >
                  Mostra altri prodotti ({products.length - visible})
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
